"use client";

import { useEffect } from "react";
import { configureFlareClient } from "@/utils/monitoring/configureFlareClient";
import { shouldFilterFlareError } from "@/utils/monitoring/flareErrorFilter";
import { rejectionReasonToError } from "@/utils/monitoring/rejectionReasonToError";
import { safelyReportToFlare } from "@/utils/monitoring/safelyReportToFlare";
import { installStaleDeploymentRecovery } from "@/utils/monitoring/staleDeploymentRecovery";
import type { Attributes } from "@flareapp/js";

if (typeof window !== "undefined") {
  installStaleDeploymentRecovery(window);
}

export function FlareProvider() {
  useEffect(() => {
    let cleanupWindowErrorHandlers: (() => void) | undefined;
    let cancelled = false;

    if (
      typeof window === "undefined" ||
      !process.env.NEXT_PUBLIC_FLARE_API_KEY
    ) {
      return;
    }

    if (process.env.NODE_ENV === "development") {
      window.flare = {
        report: async (error: Error, context?: Attributes) => {
          if (
            shouldFilterFlareError(error, error.message, {
              filterTransientBrowserErrors: true,
              filterExpectedRequestErrors: true,
            })
          ) {
            return;
          }

          console.group("🔥 Flare Error Report (Development Mode)");
          console.error("Error:", error);
          if (context) console.log("Context:", context);
          console.groupEnd();
        },
      };
      return;
    }

    const initializeFlare = async () => {
      try {
        const { flare } = await import("@flareapp/js");

        configureFlareClient(flare);

        flare.light(process.env.NEXT_PUBLIC_FLARE_API_KEY);

        if (cancelled) {
          return;
        }

        // @flareapp/js ignores ErrorEvents without event.error; preserve those
        // reports without double-reporting normal errors.
        const handleWindowError = (event: ErrorEvent) => {
          if (
            shouldFilterFlareError(event.error ?? null, event.message, {
              source: event.filename,
              filterTransientBrowserErrors: true,
              filterExpectedRequestErrors: true,
            })
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          if (event.error || !event.message) {
            return;
          }

          const error = new Error(event.message);

          safelyReportToFlare(flare, error, {
            source: event.filename,
            line: event.lineno,
            column: event.colno,
          });
        };

        // SDK hooks perform the actual filtering; this listener suppresses the
        // browser warning and any later listeners for dropped promise rejections.
        const handlePromiseRejection = (event: PromiseRejectionEvent) => {
          const error = rejectionReasonToError(event.reason);

          if (
            shouldFilterFlareError(error, error.message, {
              filterTransientBrowserErrors: true,
              filterExpectedRequestErrors: true,
            })
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
        };

        window.addEventListener("error", handleWindowError, true);
        window.addEventListener(
          "unhandledrejection",
          handlePromiseRejection,
          true,
        );
        window.flare = flare;
        cleanupWindowErrorHandlers = () => {
          window.removeEventListener("error", handleWindowError, true);
          window.removeEventListener(
            "unhandledrejection",
            handlePromiseRejection,
            true,
          );
        };
      } catch (error) {
        console.error("Failed to initialize Flare:", error);
      }
    };

    const timeoutId = setTimeout(initializeFlare, 100);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      cleanupWindowErrorHandlers?.();
    };
  }, []);

  return null;
}
